import { ceil, cloneDeep, flatten } from 'lodash';
import { getFramework, IFramework, resetGroupLayerRotate } from '@/framework';
import type { EdgelessBackgroundLayer } from '@/layers/background-layer/edgeless-layer';
import { clearImageEdgeBlank } from '@/utils/clear-image-edge-blank';
import { getRotatePosition } from '@/utils/collision-kit';
import { adjustCropImgPosition } from '@/utils/crop-img-kit';
import { loadImageAsync } from '@/utils/file-kit';
import { computerBgRatio, getLayerBoundingBox } from '@/utils/layer-kit';
import {
    flipPoint,
    getLineIntegerPoints,
    pointRelation,
    pointScale,
    revertPointY,
    rotatePoint,
    translateYReverse,
} from '@/utils/math-kit';
import { traverseTreePostOrder, traverseTrees } from '@/utils/tree-kit';
import {
    DirectionType,
    IBackgroundLayerProps,
    IImageLayerProps,
    ILayerProps,
    IMainLayerProps,
    IPoint,
    ISize,
    LayerType,
    MaterialIdsType,
    StretchOptionType,
} from '@/version-types';
import { historyKey, IHistoryItemData, resetStage } from './history';
import { getStageCropStore, IStore, watchStageCropStore } from './models/crop-stage-state';
import { CROP_STAGE_EVENT } from './types';
import {
    adjustMainLayerPositionAfterScaleOut,
    computeAutoScale,
    getLayerCenterPositionRelateToMainLayerCenterPosition,
    getMainLayerCenterPointRelativeStageCenterPoint,
    getMainLayerCenterPosition,
    getMainLayerSize,
    getMousePointPositionInMainLayerCenterPoint,
    getStageAndMainLayerCenterPoint,
    isMainLayerOutOfRange,
    scaleMainLayer,
    scaleMainLayerCenterPointByStageCenterPoint,
    stageSizeChangeAndMoveMainLayer,
    toIntByDirection,
} from './utils';

const filterAndUpdateGroupLayer = (layers: ILayerProps[], callback: Function) => {
    layers.forEach((layer) => {
        if (layer.layerType === LayerType.GROUP_LAYER) {
            traverseTreePostOrder(layer, (layer: ILayerProps) => {
                if (layer.layerType === LayerType.GROUP_LAYER) {
                    const positions: IPoint[] = [];
                    (layer.children || []).forEach((child) => {
                        const position = getRotatePosition(child);
                        positions.push(...position);
                    });
                    if (!positions.length) return;
                    resetGroupLayerRotate(layer, positions);
                    callback(layer);
                }
            });
        }
    });
};

export const backgroundStageChange = (layer: ILayerProps, stageWidth: number, stageHeight: number) => {
    const data = layer.data as IBackgroundLayerProps;
    if (data.stretchOption === StretchOptionType.cover) {
        const { naturalHeight: height, naturalWidth: width } = data;
        const { widthRatio, heightRatio } = computerBgRatio({
            imgWidth: width,
            imgHeight: height,
            stageHeight: stageHeight,
            stageWidth: stageWidth,
        });
        (layer.data as IBackgroundLayerProps).imageTransform.widthRatio = widthRatio;
        (layer.data as IBackgroundLayerProps).imageTransform.heightRatio = heightRatio;
    }
    layer.width = stageWidth;
    layer.height = stageHeight;
    return;
};

const formatMainLayerToInt = (mainLayer: ILayerProps, isOutOfStage = false) => {
    const newMainLayer = cloneDeep(mainLayer);
    newMainLayer.width = Math.trunc(mainLayer.width);
    newMainLayer.height = Math.trunc(mainLayer.height);
    newMainLayer.left = Math.trunc(mainLayer.left);
    newMainLayer.top = Math.trunc(mainLayer.top);

    if (!isMainLayerOutOfRange(newMainLayer, isOutOfStage)) {
        return newMainLayer;
    } else {
        newMainLayer.left = Math.floor(mainLayer.left);
        newMainLayer.top = Math.floor(mainLayer.top);
        if (!isMainLayerOutOfRange(newMainLayer, isOutOfStage)) {
            console.log('1===');
            return newMainLayer;
        }
    }

    if (isMainLayerOutOfRange(newMainLayer, isOutOfStage)) {
        newMainLayer.left = Math.ceil(mainLayer.left);
        newMainLayer.top = Math.ceil(mainLayer.top);

        if (!isMainLayerOutOfRange(newMainLayer, isOutOfStage)) {
            console.log('2===');
            return newMainLayer;
        }
    }

    if (isMainLayerOutOfRange(newMainLayer, isOutOfStage)) {
        newMainLayer.left = Math.trunc(mainLayer.left);
        newMainLayer.top = Math.floor(mainLayer.top);

        if (!isMainLayerOutOfRange(newMainLayer, isOutOfStage)) {
            console.log('2===');
            return newMainLayer;
        }
    }

    if (isMainLayerOutOfRange(newMainLayer, isOutOfStage)) {
        newMainLayer.left = Math.floor(mainLayer.left);
        newMainLayer.top = Math.trunc(mainLayer.top);

        if (!isMainLayerOutOfRange(newMainLayer, isOutOfStage)) {
            console.log('3===');
            return newMainLayer;
        }
    }
    console.log(
        'formatMainLayerToInt======',
        mainLayer.left,
        mainLayer.top,
        mainLayer.width,
        mainLayer.height,
        newMainLayer.left,
        newMainLayer.top,
        newMainLayer.width,
        newMainLayer.height,
    );
    return mainLayer;
};

export class CropStagePlugin {
    /** 编辑器实例 */
    private framework: IFramework;
    private store: IStore;
    private originLayers: ILayerProps[] = [];
    private cropFrameId: number | null = null;
    private cropFrameMaskEnabled = true;
    private cropFrameClipEnabled = true;
    private cropOriginViewport: number[] | null = null;
    // private cropOriginIsAutoScale = true;

    constructor(identity: string) {
        this.framework = getFramework(identity);
        this.store = getStageCropStore(this.framework.identity);

        watchStageCropStore(identity, (state) => {
            this.store = state;
        });
    }

    private addOrUpdateLayerBaseInfo(layer: ILayerProps, onlyUpdateBaseInfo = true) {
        this.framework.service.frame.addOrUpdateLayer(layer, undefined, onlyUpdateBaseInfo);
        if (onlyUpdateBaseInfo) {
            this.framework.service.edgelessCanvas.requestRenderAll();
        }
    }

    open(isOutOfStage = false) {
        if (this.store.isCropStage) return;
        const originIndex = this.framework.service.frame.getActiveFrameId();

        this.originLayers = cloneDeep(this.framework.service.frame.getLayers(originIndex));
        this.closeFrameMaskAndClip(originIndex);
        this.setBackgroundDrawExtraBackground(false, originIndex);
        this.syncCropFrameViewport(originIndex);
        this.store.setIsCropStage(true);
        this.store.setIsOutOfStage(isOutOfStage);
        this.framework.service.frame.delActiveLayer();
        this.framework.service.editor.setDisablePick(true);
        // 进入裁剪时，需要禁用撤销重做
        this.framework.service.history.setRedoUndo(false);
    }

    private reset() {
        this.setBackgroundDrawExtraBackground(true);
        this.restoreFrameMaskAndClip();
        this.framework.service.edgelessCanvas.setIsAutoScale(true);
        this.store.setIsCropStage(false);
        this.cropOriginViewport = null;
        this.emitPreviewStageSizeChange(null);
        this.framework.service.editor.setDisablePick(false);
        // 退出裁剪时，恢复撤销重做
        this.framework.service.history.setRedoUndo(true);
        this.originLayers = [];
    }

    private closeFrameMaskAndClip(frameId: number) {
        const frame = this.framework.service.frame.get(frameId);
        if (!frame) return;

        this.cropFrameId = frameId;
        this.cropFrameMaskEnabled = frame.isMaskEnabled();
        this.cropFrameClipEnabled = frame.isClipEnabled();
        frame.setMaskEnabled(false);
        frame.setClipEnabled(false);
    }

    private restoreFrameMaskAndClip() {
        if (this.cropFrameId === null) return;

        const frame = this.framework.service.frame.get(this.cropFrameId);
        frame?.setMaskEnabled(this.cropFrameMaskEnabled);
        frame?.setClipEnabled(this.cropFrameClipEnabled);

        this.cropFrameId = null;
        this.cropFrameMaskEnabled = true;
        this.cropFrameClipEnabled = true;
    }

    private setBackgroundDrawExtraBackground(enable: boolean, frameId = this.cropFrameId) {
        if (frameId === null) return;

        const backgroundLayer = this.framework.service.frame.getBackgroundLayer(frameId);
        if (!backgroundLayer) return;

        const backgroundObject = this.framework.service.edgelessCanvas.getByLayerId(
            backgroundLayer.id,
        ) as EdgelessBackgroundLayer | null;
        backgroundObject?.setDrawExtraBackground(enable);
    }

    emitPreviewStageSizeChange(size: ISize | null) {
        this.framework.service.events.emit(CROP_STAGE_EVENT.PREVIEW_STAGE_SIZE_CHANGE, size);
    }

    onPreviewStageSizeChange(fn: (size: ISize | null) => void) {
        return this.framework.service.events.on(CROP_STAGE_EVENT.PREVIEW_STAGE_SIZE_CHANGE, fn);
    }

    syncCropFrameViewport(frameId: number, stageSize?: Pick<ILayerProps, 'width' | 'height'>) {
        const frame = this.framework.service.frame.get(frameId);
        const mainLayer = this.framework.service.frame.getMainLayer(frameId);
        if (!frame || !mainLayer) return;

        const { width: currentWidth, height: currentHeight } = this.framework.service.edgelessCanvas.getSize();
        if (!currentWidth || !currentHeight) return;

        if (!this.cropOriginViewport) {
            this.cropOriginViewport = [...this.framework.service.edgelessCanvas.viewportTransform];
            // this.cropOriginIsAutoScale = this.framework.service.edgelessCanvas.isAutoScale;
        }

        const stageWidth = stageSize?.width ?? mainLayer.width;
        const stageHeight = stageSize?.height ?? mainLayer.height;

        const stageScale = computeAutoScale(
            currentWidth - 160,
            currentHeight - 160,
            stageWidth,
            stageHeight,
            this.framework.service.edgelessCanvas.fitPadding[0],
        );

        this.store.setCurrentCropStageScale(stageScale);
        this.framework.service.edgelessCanvas.setIsAutoScale(false);
        this.framework.service.edgelessCanvas.setViewportTransform([
            stageScale,
            0,
            0,
            stageScale,
            (currentWidth - stageWidth * stageScale) / 2 - frame.left * stageScale,
            (currentHeight - stageHeight * stageScale) / 2 - frame.top * stageScale,
        ]);
        this.framework.service.edgelessCanvas.requestRenderAll();

        return stageScale;
    }

    close() {
        resetStage(this.framework, this.originLayers);
        this.reset();
    }

    apply(newData: ILayerProps[], trackFunc: any) {
        this.framework.service.edgelessCanvas.setIsAutoScale(true);
        this.framework.service.history.write<IHistoryItemData>({
            type: historyKey,
            data: {
                oldData: this.originLayers,
                newData,
                isAdjust: true,
            },
            trackFunc,
        });
        this.reset();
    }

    setIsStageAutoCrop(canAutoCrop: boolean) {
        this.store.setIsStageAutoCrop(canAutoCrop);
    }

    watch(callback: Parameters<typeof watchStageCropStore>['1']) {
        return watchStageCropStore(this.framework.identity, callback);
    }

    async beforeCrop(layer: ILayerProps, scale: number) {
        this.framework.service.behavior.get(layer)?.onBeforeCrop?.(layer, scale);
    }

    /**
     * 舞台宽高改版
     *
     * @param width - 舞台宽度
     * @param height - 舞台高度
     */
    changeSize(width: number, height: number, stageIndex?: number) {
        const originIndex = stageIndex || this.framework.service.frame.getActiveFrameId();
        const mainLayer = this.framework.service.frame.getMainLayer(originIndex);
        const layers = this.framework.service.frame.getLayers(originIndex);

        // 防御性代码
        if (!mainLayer) {
            return;
        }

        const { top: originMainLayerTop, left: originMainLayerLeft, rotate } = mainLayer;
        const data = mainLayer.data as IImageLayerProps;
        const { naturalWidth: natureMainLayerWidth, naturalHeight: natureMainLayerHeight, imageTransform } = data;
        const { scale: originScale } = imageTransform;
        // 调整主图的位置
        const widthRadio = width / natureMainLayerWidth;
        const heightRadio = height / natureMainLayerHeight;
        const scale = widthRadio > heightRadio ? widthRadio : heightRadio;

        imageTransform.scale = scale;
        mainLayer.left = (width - natureMainLayerWidth * scale) / 2;
        mainLayer.top = (height - natureMainLayerHeight * scale) / 2;
        mainLayer.width = width;
        mainLayer.height = height;
        mainLayer.rotate = 0;
        imageTransform.flipH = false;
        imageTransform.flipV = false;
        imageTransform.rotate = 0;

        this.addOrUpdateLayerBaseInfo({ ...mainLayer });

        // const { width: currentWidth, height: currentHeight } = this.framework.service.edgelessCanvas.getSize();

        // const stageScale = computeAutoScale(
        //     currentWidth - 160,
        //     currentHeight - 160,
        //     mainLayer.width,
        //     mainLayer.height,
        //     this.framework.service.edgelessCanvas.fitPadding[0],
        // );

        // this.store.setCurrentCropStageScale(stageScale);

        traverseTrees(layers, (layer: ILayerProps) => {
            if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                backgroundStageChange(layer, width, height);
                this.addOrUpdateLayerBaseInfo({ ...layer });
                return;
            }
            if (layer.layerType === LayerType.MAIN_LAYER || (layer.children && layer.children.length > 0)) {
                return;
            }
            const radio = imageTransform.scale;
            const { left: mainLayerLeft, top: mainLayerTop } = mainLayer;
            const { left: layerLeft, top: layerTop, width: layerWidth, height: layerHeight } = layer;
            const layerRelativeMainLayerPoint = {
                x: layerLeft + layerWidth / 2 - (originMainLayerLeft + (natureMainLayerWidth * originScale) / 2),
                y: -(layerTop + layerHeight / 2 - (originMainLayerTop + (natureMainLayerHeight * originScale) / 2)),
            };
            const newLayerRelativeMainLayerPoint = {
                x: layerRelativeMainLayerPoint.x * (radio / originScale),
                y: layerRelativeMainLayerPoint.y * (radio / originScale),
            };
            layer.width = layerWidth * (radio / originScale);
            layer.height = layerHeight * (radio / originScale);
            layer.rotate = layer.rotate - rotate;
            const newLayerPosition = {
                x:
                    newLayerRelativeMainLayerPoint.x +
                    (mainLayerLeft + (natureMainLayerWidth * radio) / 2) -
                    layer.width / 2,
                y:
                    -newLayerRelativeMainLayerPoint.y +
                    (mainLayerTop + (natureMainLayerHeight * radio) / 2) -
                    layer.height / 2,
            };
            layer.left = newLayerPosition.x;
            layer.top = newLayerPosition.y;
            this.framework.service.behavior.get(layer)?.onBeforeCrop?.(layer, radio / originScale);
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });

        filterAndUpdateGroupLayer(layers, (layer: ILayerProps) => {
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });

        this.syncCropFrameViewport(originIndex);
    }

    rotateStage(rotate: number, stageIndex?: number) {
        const originIndex = stageIndex === undefined ? this.framework.service.frame.getActiveFrameId() : stageIndex;
        const mainLayer = this.framework.service.frame.getMainLayer(originIndex);
        const layers = this.framework.service.frame.getLayers(originIndex);

        // 防御性代码
        if (!mainLayer) {
            return;
        }

        const {
            top: originMainLayerTop,
            left: originMainLayerLeft,
            width: originStageWidth,
            height: originStageHeight,
            rotate: originStageRotate,
        } = mainLayer;
        const data = mainLayer.data as IImageLayerProps;
        const { naturalWidth: natureMainLayerWidth, naturalHeight: natureMainLayerHeight, imageTransform } = data;
        const { scale: originMainLayerScale } = imageTransform;
        // 主图和舞台中心点的相对位置
        const { originRelativeStagePoint } = getStageAndMainLayerCenterPoint(mainLayer);

        originRelativeStagePoint.y = -originRelativeStagePoint.y;

        const rotateRelativeStagePoint = rotatePoint(originRelativeStagePoint, -rotate);

        mainLayer.width = originStageHeight;
        mainLayer.height = originStageWidth;
        mainLayer.rotate = (originStageRotate + rotate) % 360;
        // 根据相对位置重新计算left top
        const newMainLayerPosition = {
            x: rotateRelativeStagePoint.x + originStageHeight / 2 - (natureMainLayerWidth * originMainLayerScale) / 2,
            y: -rotateRelativeStagePoint.y + originStageWidth / 2 - (natureMainLayerHeight * originMainLayerScale) / 2,
        };
        mainLayer.left = newMainLayerPosition.x;
        mainLayer.top = newMainLayerPosition.y;

        this.addOrUpdateLayerBaseInfo({ ...mainLayer });

        // const { width: currentWidth, height: currentHeight } = this.framework.service.edgelessCanvas.getSize();

        // const stageScale = computeAutoScale(
        //     currentWidth - 160,
        //     currentHeight - 160,
        //     mainLayer.width,
        //     mainLayer.height,
        //     this.framework.service.edgelessCanvas.fitPadding[0],
        // );

        // this.store.setCurrentCropStageScale(stageScale);

        traverseTrees(layers, (layer: ILayerProps) => {
            if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                backgroundStageChange(layer, mainLayer.width, mainLayer.height);
                this.addOrUpdateLayerBaseInfo({ ...layer });
                return;
            }
            if (layer.layerType === LayerType.MAIN_LAYER || (layer.children && layer.children.length > 0)) {
                return;
            }
            layer.rotate = (layer.rotate + rotate) % 360;
            const { left: mainLayerLeft, top: mainLayerTop } = mainLayer;
            const { left: layerLeft, top: layerTop, width: layerWidth, height: layerHeight } = layer;
            // 主图和图层的相对位置
            const layerRelativeMainLayerPoint = {
                x:
                    layerLeft +
                    layerWidth / 2 -
                    (originMainLayerLeft + (natureMainLayerWidth * originMainLayerScale) / 2),
                y: -(
                    layerTop +
                    layerHeight / 2 -
                    (originMainLayerTop + (natureMainLayerHeight * originMainLayerScale) / 2)
                ),
            };
            // 相对位置进行旋转
            const rotateLayerPosition = rotatePoint(layerRelativeMainLayerPoint, -rotate);
            //  转换后图层位置
            const newLayerPosition = {
                x:
                    rotateLayerPosition.x +
                    (mainLayerLeft + (natureMainLayerWidth * originMainLayerScale) / 2) -
                    layerWidth / 2,
                y:
                    -rotateLayerPosition.y +
                    (mainLayerTop + (natureMainLayerHeight * originMainLayerScale) / 2) -
                    layerHeight / 2,
            };
            layer.left = newLayerPosition.x;
            layer.top = newLayerPosition.y;
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });

        this.syncCropFrameViewport(originIndex);
    }

    rotateAllLayers(rotate: number, mainLayer: ILayerProps, layers: ILayerProps[], originScale: number) {
        const { width: originStageWidth, height: originStageHeight } = mainLayer;

        // 如果超过90度就调整刚好90
        let addRotate = rotate;
        const direction = addRotate ? addRotate / Math.abs(addRotate) : 1;
        if (Math.abs(mainLayer.rotate) === 180) {
            addRotate = mainLayer.rotate * addRotate > 0 ? 0 : addRotate;
        } else if (Math.abs(addRotate + mainLayer.rotate) > 180) {
            addRotate = (Math.abs(addRotate) - (Math.abs(addRotate + mainLayer.rotate) - 180)) * direction;
        }
        rotate = addRotate;
        const data = mainLayer.data as IImageLayerProps;
        const { naturalWidth: natureMainLayerWidth, naturalHeight: natureMainLayerHeight, imageTransform } = data;
        const { scale: originMainLayerScale } = imageTransform;
        const { originRelativeStagePoint, originMainLayerCenterPoint } = getStageAndMainLayerCenterPoint(mainLayer);
        originRelativeStagePoint.y = -originRelativeStagePoint.y; // 坐标方向不同
        // 主图中心点以舞台中心点做旋转
        const rotateMainLayerPosition = rotatePoint(
            {
                x: originRelativeStagePoint.x,
                y: originRelativeStagePoint.y,
            },
            -rotate,
        );
        const newMainLayerPosition = {
            x: rotateMainLayerPosition.x + originStageWidth / 2 - (natureMainLayerWidth * originMainLayerScale) / 2,
            y: -rotateMainLayerPosition.y + originStageHeight / 2 - (natureMainLayerHeight * originMainLayerScale) / 2,
        };
        mainLayer.left = newMainLayerPosition.x;
        mainLayer.top = newMainLayerPosition.y;
        mainLayer.rotate = mainLayer.rotate + rotate;

        const { scale, movePoint } = adjustCropImgPosition({
            mainLayerSize: {
                width: originStageWidth,
                height: originStageHeight,
            },
            adjustLayerSize: {
                width: natureMainLayerWidth * originMainLayerScale,
                height: natureMainLayerHeight * originMainLayerScale,
            },
            adjustLayerRotate: mainLayer.rotate,
            adjustLayerRelativePosition: rotatePoint(
                {
                    x: -originRelativeStagePoint.x,
                    y: -originRelativeStagePoint.y,
                },
                mainLayer.rotate,
            ),
        });
        if (scale === 1) {
            mainLayer.left =
                originRelativeStagePoint.x +
                movePoint.x +
                originStageWidth / 2 -
                (natureMainLayerWidth * originMainLayerScale) / 2;
            mainLayer.top =
                -originRelativeStagePoint.y -
                movePoint.y +
                originStageHeight / 2 -
                (natureMainLayerHeight * originMainLayerScale) / 2;
        } else {
            imageTransform.scale = originMainLayerScale * scale;
            mainLayer.left =
                mainLayer.left +
                (natureMainLayerWidth * originMainLayerScale - natureMainLayerWidth * originMainLayerScale * scale) / 2;
            mainLayer.top =
                mainLayer.top +
                (natureMainLayerHeight * originMainLayerScale - natureMainLayerHeight * originMainLayerScale * scale) /
                    2;
        }

        this.addOrUpdateLayerBaseInfo({ ...mainLayer });

        traverseTrees(layers, (layer: ILayerProps) => {
            if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                backgroundStageChange(layer, mainLayer.width, mainLayer.height);
                this.addOrUpdateLayerBaseInfo({ ...layer });
                return;
            }
            if (layer.layerType === LayerType.MAIN_LAYER || (layer.children && layer.children.length > 0)) {
                return;
            }
            const { left: layerLeft, top: layerTop, width: layerWidth, height: layerHeight } = layer;
            const layerCenterPoint = {
                x: layerLeft + layerWidth / 2,
                y: layerTop + layerHeight / 2,
            };
            // 图层中心点和主图的相对位置
            const relativeMainLayerPoint = pointRelation(originMainLayerCenterPoint, layerCenterPoint);
            relativeMainLayerPoint.y = -relativeMainLayerPoint.y;
            // 图层中心点以舞台中心点做旋转
            const rotateRelativeMainLayerPoint = rotatePoint(relativeMainLayerPoint, -rotate);
            // 旋转后图层的中心点位置
            const rotateLayerCenterPosition = {
                x: rotateRelativeMainLayerPoint.x,
                y: -rotateRelativeMainLayerPoint.y,
            };
            const newMainLayerCenterPoint = {
                x: mainLayer.left + (natureMainLayerWidth * imageTransform.scale) / 2,
                y: mainLayer.top + (natureMainLayerHeight * imageTransform.scale) / 2,
            };
            // 图层和新主图的相对位置
            const newRelationMainLayer = pointScale(rotateLayerCenterPosition, scale);
            layer.width = layerWidth * scale;
            layer.height = layerHeight * scale;
            const newLayerPosition = {
                x: newRelationMainLayer.x + newMainLayerCenterPoint.x - layer.width / 2,
                y: newRelationMainLayer.y + newMainLayerCenterPoint.y - layer.height / 2,
            };
            layer.left = newLayerPosition.x;
            layer.top = newLayerPosition.y;
            layer.rotate = layer.rotate + rotate;
            this.framework.service.behavior.get(layer)?.onBeforeCrop?.(layer, scale);
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });
        filterAndUpdateGroupLayer(layers, (layer: ILayerProps) => {
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });
    }

    // 移动主图
    moveStageOperate(
        lastPoint: IPoint,
        movePoint: IPoint,
        mainLayer: ILayerProps,
        layers: ILayerProps[],
        stageScale: number,
    ) {
        const { flipH = false, flipV = false, rotate } = mainLayer;
        const points = getLineIntegerPoints(
            lastPoint.x,
            lastPoint.y,
            lastPoint.x + movePoint.x,
            lastPoint.y + movePoint.y,
        );
        let newPoint = { x: lastPoint.x, y: lastPoint.y };

        const moveLayers = (layers: ILayerProps[], moveDistance: IPoint) => {
            traverseTrees(layers, (layer: ILayerProps) => {
                if (
                    layer.layerType === LayerType.MAIN_LAYER ||
                    layer.layerType === LayerType.BACKGROUND_LAYER ||
                    (layer.children && layer.children.length > 0)
                ) {
                    return;
                }

                layer.left = layer.left + moveDistance.x;
                layer.top = layer.top + moveDistance.y;

                this.addOrUpdateLayerBaseInfo({ ...layer });
            });
        };
        for (let i = points.length - 1; i >= 0; i--) {
            const displacement = {
                x: 0,
                y: 0,
            };
            const newMainLayer = cloneDeep(mainLayer);
            const point = points[i];
            displacement.x = point.x - lastPoint.x;
            displacement.y = point.y - lastPoint.y;
            // 翻转后的位置方向不一样 需要处理下
            const currentMoveDistance = translateYReverse(
                flipPoint(
                    translateYReverse({
                        x: displacement.x / stageScale,
                        y: displacement.y / stageScale,
                    }),
                    flipH,
                    flipV,
                ),
            ) as IPoint;
            newMainLayer.left = newMainLayer.left + currentMoveDistance.x;
            newMainLayer.top = newMainLayer.top + currentMoveDistance.y;
            const isOut = isMainLayerOutOfRange(newMainLayer, this.store.isOutOfStage);
            if (!isOut) {
                moveLayers(layers, currentMoveDistance);

                mainLayer.left = newMainLayer.left;
                mainLayer.top = newMainLayer.top;
                newPoint = { x: point.x, y: point.y };
                break;
            }

            // 如果超出范围 那就只移动x或只移动y看看是否超出 没超出照样移动就好了
            if (isOut) {
                // 移动的距离转化为图片坐标系
                // 移动的距离转化为图片坐标系
                const imageOffsetPosition = flipPoint(
                    rotatePoint(translateYReverse(currentMoveDistance), rotate),
                    flipH,
                    flipV,
                );

                // 只取一边的位移  然后再转为图层的坐标系 先试试x能不能移得动 然后再试试y能不能移得动
                const onlyOffsetX = translateYReverse(
                    flipPoint(
                        rotatePoint(
                            {
                                x: imageOffsetPosition.x,
                                y: 0,
                            },
                            -rotate,
                        ),
                        flipH,
                        flipV,
                    ),
                );

                newMainLayer.left = mainLayer.left + onlyOffsetX.x;
                newMainLayer.top = mainLayer.top + onlyOffsetX.y;
                if (!isMainLayerOutOfRange(newMainLayer, this.store.isOutOfStage)) {
                    moveLayers(layers, onlyOffsetX);
                    mainLayer.left = newMainLayer.left;
                    mainLayer.top = newMainLayer.top;
                    newPoint = { x: point.x, y: point.y };
                    break;
                }

                const onlyOffsetY = translateYReverse(
                    flipPoint(
                        rotatePoint(
                            {
                                x: 0,
                                y: imageOffsetPosition.y,
                            },
                            -rotate,
                        ),
                        flipH,
                        flipV,
                    ),
                );

                newMainLayer.left = mainLayer.left + onlyOffsetY.x;
                newMainLayer.top = mainLayer.top + onlyOffsetY.y;
                if (!isMainLayerOutOfRange(newMainLayer, this.store.isOutOfStage)) {
                    moveLayers(layers, onlyOffsetY);
                    mainLayer.left = newMainLayer.left;
                    mainLayer.top = newMainLayer.top;
                    newPoint = { x: point.x, y: point.y };
                    break;
                }
            }
        }

        const resultMainLayer = formatMainLayerToInt({ ...mainLayer });
        this.addOrUpdateLayerBaseInfo({ ...resultMainLayer });

        return {
            newMainLayer: resultMainLayer,
            newLayers: layers,
            newPoint,
        };
    }

    // 旋转的时候是以舞台为中心的那个点旋转图片
    rotateMainImageOperate(rotate: number, mainLayer: ILayerProps, layers: ILayerProps[]) {
        const { width, height, rotate: originMainLayerRotate, flipH = false, flipV = false } = mainLayer;
        const data = mainLayer.data as IImageLayerProps;
        const { naturalWidth: natureMainLayerWidth, naturalHeight: natureMainLayerHeight, imageTransform } = data;
        const { scale: originMainLayerScale } = imageTransform;

        const currentRotate = (originMainLayerRotate + rotate) % 360;

        const mainLayerSize = {
            width: natureMainLayerWidth * originMainLayerScale,
            height: natureMainLayerHeight * originMainLayerScale,
        };

        // 先获取主图中心点的位置相对于舞台中心点的相对位置 旋转下得到新的中心位置  再转换为新的left top
        const mainLayerCenterPointRelativeStageCenterPoint = getMainLayerCenterPointRelativeStageCenterPoint(mainLayer);
        const newMainLayerCenterPoint = flipPoint(
            revertPointY(rotatePoint(mainLayerCenterPointRelativeStageCenterPoint, -rotate)),
            flipH,
            flipV,
        );

        const newMainLayerPosition = {
            x: newMainLayerCenterPoint.x + width / 2 - mainLayerSize.width / 2,
            y: newMainLayerCenterPoint.y + height / 2 - mainLayerSize.height / 2,
        };

        const newMainLayer = {
            ...cloneDeep(mainLayer),
            left: newMainLayerPosition.x,
            top: newMainLayerPosition.y,
            rotate: currentRotate,
        };

        const newLayers = cloneDeep(layers);

        // isZoomOut 是否缩放
        const isOutAndScaleLayer = (outLayer: ILayerProps, isZoomOut: boolean = false) => {
            const isOut = isMainLayerOutOfRange(outLayer, this.store.isOutOfStage);
            // 超出了就加scale
            // 直到不超出为止
            // 如果是顺时针旋转超出了

            // 在初始角度和上一个角度之间就判断为往回走 不然就是往前走
            const { imageTransform, naturalWidth, naturalHeight } = outLayer.data as IImageLayerProps;
            const { scale: newMainLayerScale } = imageTransform;
            const newMainImageWidth = naturalWidth * newMainLayerScale;
            const newMainImageHeight = naturalHeight * newMainLayerScale;
            if (isOut && !isZoomOut) {
                const newWidth =
                    newMainImageWidth < newMainImageHeight
                        ? newMainImageWidth + 1
                        : ceil((newMainImageHeight + 1) / (newMainImageHeight / newMainImageWidth));

                // 放大 还要以画布中心点缩放
                const newScale = newWidth / naturalWidth;
                scaleMainLayerCenterPointByStageCenterPoint(outLayer, newScale / newMainLayerScale);
                imageTransform.scale = newScale;
                if (isMainLayerOutOfRange(outLayer, this.store.isOutOfStage)) {
                    isOutAndScaleLayer(outLayer);
                }
            } else if (!isOut && originMainLayerScale && originMainLayerScale < newMainLayerScale) {
                const oldLayer = cloneDeep(outLayer);
                const newWidth =
                    newMainImageWidth < newMainImageHeight
                        ? newMainImageWidth - 1
                        : ceil((newMainImageHeight - 1) / (newMainImageHeight / newMainImageWidth));

                // 缩小 还要以画布中心点缩放
                const newScale = newWidth / naturalWidth;
                scaleMainLayerCenterPointByStageCenterPoint(outLayer, newScale / newMainLayerScale);
                imageTransform.scale = newScale;

                if (!isMainLayerOutOfRange(outLayer, this.store.isOutOfStage)) {
                    isOutAndScaleLayer(outLayer, true);
                } else {
                    Object.assign(outLayer, oldLayer);
                }
            }
        };

        isOutAndScaleLayer(newMainLayer);

        // 计算初始时单个图层中心点相对于主图中心的的位置
        // 旋转以后再计算两个
        // 1、以新的主图中心点计算单个图层中心点相对于主图中心的的位置, 如果有有scale还要乘以scale
        // 2、图层要跟着旋转rotate度， 图层也要乘以scale   scale(newScale / originMainLayerScale)为前后缩放差值
        traverseTrees(newLayers, (layer: ILayerProps) => {
            if (
                layer.layerType === LayerType.MAIN_LAYER ||
                layer.layerType === LayerType.BACKGROUND_LAYER ||
                layer.layerType === LayerType.GROUP_LAYER
            ) {
                return;
            }

            const relativePosition = getLayerCenterPositionRelateToMainLayerCenterPosition(layer, mainLayer);

            const subScale = (newMainLayer.data as IImageLayerProps).imageTransform.scale / originMainLayerScale;
            const newRelativePosition = revertPointY(
                rotatePoint(
                    revertPointY({
                        x: relativePosition.x * subScale,
                        y: relativePosition.y * subScale,
                    }),
                    -rotate,
                ),
            );

            const newMainLayerCenterPoint = getMainLayerCenterPosition(newMainLayer);
            this.framework.service.frame.scaleLayer(layer, layer.width * subScale - layer.width);

            layer.left = newRelativePosition.x + newMainLayerCenterPoint.x - layer.width / 2;
            layer.top = newRelativePosition.y + newMainLayerCenterPoint.y - layer.height / 2;
            layer.rotate = layer.rotate + rotate;
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });

        this.addOrUpdateLayerBaseInfo({ ...newMainLayer });
    }

    scaleMainLayerOperate(
        mainLayer: ILayerProps,
        layers: ILayerProps[],
        direction: DirectionType,
        mousePointByBrowser: IPoint,
        stageScale: number,
        scaleDistance: number,
    ) {
        // 以鼠标的点为缩放的中心点缩放主图
        // 先鼠标的点相对于主图中心点的位置
        // 缩放图片和 相对位置
        // 判断超出情况 做相应调整  图层的中心点向画布的中心点靠近
        // 调整图层位置
        // 1、获取鼠标垫位置相对于主图中心点的位置(坐标系是舞台的左上角)
        const { mousePointRelativeMainLayerCenterPoint, mousePointRelativeStage } =
            getMousePointPositionInMainLayerCenterPoint(mousePointByBrowser, mainLayer, stageScale);

        const runScaleByStep = (toScaleLayer: ILayerProps, step: number) => {
            // 2、缩放主图
            const scale = scaleMainLayer(toScaleLayer, direction, step);

            // 3、算出缩放后新的主图中心点的位置 然后算出主图的left top
            const { data, flipH = false, flipV = false } = toScaleLayer;
            const { imageTransform, naturalWidth, naturalHeight } = data as IImageLayerProps;
            const { scale: mainLayerScale } = imageTransform;
            const mainLayerWidth = naturalWidth * mainLayerScale;
            const mainLayerHeight = naturalHeight * mainLayerScale;
            const newMainLayerCenterPoint = flipPoint(
                {
                    x: mousePointRelativeStage.x - mousePointRelativeMainLayerCenterPoint.x * scale,
                    y: mousePointRelativeStage.y - mousePointRelativeMainLayerCenterPoint.y * scale,
                },
                flipH,
                flipV,
            );

            const newMainLayerPosition = {
                x: newMainLayerCenterPoint.x - mainLayerWidth / 2,
                y: newMainLayerCenterPoint.y - mainLayerHeight / 2,
            };

            // 取整操作 不然会导致放大缩小的时候 差一个像素超出范围
            toScaleLayer.left =
                toIntByDirection(Math.abs(newMainLayerPosition.x), direction) * (newMainLayerPosition.x > 0 ? 1 : -1);
            toScaleLayer.top =
                toIntByDirection(Math.abs(newMainLayerPosition.y), direction) * (newMainLayerPosition.y > 0 ? 1 : -1);
            return toScaleLayer;
        };

        // 一步一步缩小 缩小到不能缩小或者缩小到totalStep， 如果isOut 就看能不能移动主图位置后继续缩小
        const { width: mainLayerWidth, height: mainLayerHeight } = getMainLayerSize(mainLayer);
        const totalStep =
            mainLayerWidth > mainLayerHeight
                ? ceil((mainLayerWidth * Math.abs(scaleDistance)) / 20)
                : ceil((mainLayerHeight * Math.abs(scaleDistance)) / 20);
        let lastMainLayer = cloneDeep(mainLayer);
        for (let i = totalStep; i >= 1; i = i - 1) {
            const newMainLayer = runScaleByStep(cloneDeep(mainLayer), i);
            const isOut = isMainLayerOutOfRange(newMainLayer, this.store.isOutOfStage);

            if (isOut) {
                // 以主图中心点作为坐标原点 算出画布超出的x,y, 然后将x、y转化为画布的坐标系
                const canAdjust = adjustMainLayerPositionAfterScaleOut(newMainLayer);
                if (canAdjust && !isMainLayerOutOfRange(newMainLayer, this.store.isOutOfStage)) {
                    lastMainLayer = newMainLayer;
                    break;
                }
            } else {
                lastMainLayer = newMainLayer;
                break;
            }
        }

        const newLayers = cloneDeep(layers);
        traverseTrees(newLayers, (layer: ILayerProps) => {
            if (
                layer.layerType === LayerType.MAIN_LAYER ||
                layer.layerType === LayerType.BACKGROUND_LAYER ||
                layer.layerType === LayerType.GROUP_LAYER
            ) {
                return;
            }

            const relativePosition = getLayerCenterPositionRelateToMainLayerCenterPosition(layer, mainLayer);

            const subScale =
                (lastMainLayer.data as IImageLayerProps).imageTransform.scale /
                (mainLayer.data as IImageLayerProps).imageTransform.scale;
            const newRelativePosition = {
                x: relativePosition.x * subScale,
                y: relativePosition.y * subScale,
            };
            const newMainLayerCenterPoint = getMainLayerCenterPosition(lastMainLayer);
            this.framework.service.frame.scaleLayer(layer, layer.width * subScale - layer.width);

            layer.left = newRelativePosition.x + newMainLayerCenterPoint.x - layer.width / 2;
            layer.top = newRelativePosition.y + newMainLayerCenterPoint.y - layer.height / 2;

            this.addOrUpdateLayerBaseInfo({ ...layer });
        });

        this.addOrUpdateLayerBaseInfo({ ...lastMainLayer });
    }

    moveChangeStageSizeOperate(
        params: Pick<ILayerProps, 'left' | 'top' | 'width' | 'height'>,
        originMainLayer: ILayerProps,
        originLayers: ILayerProps[],
        dataType: string,
        isStageAutoCrop: boolean,
        shouldCommit = true,
    ) {
        const { left, top, width: newWidth, height: newHeight } = params;
        const {
            left: originMainLayerLeft,
            top: originMainLayerTop,
            width: originStageWidth,
            height: originStageHeight,
        } = originMainLayer;

        const moveSteps = [] as Pick<ILayerProps, 'left' | 'top' | 'width' | 'height'>[];

        if ((dataType === 'crop-tc' || dataType === 'crop-lc') && !isStageAutoCrop) {
            if (originStageWidth <= originStageHeight) {
                const subLeft = left - originMainLayerLeft;
                const direction = subLeft > 0 ? 1 : -1;
                const radio = originStageHeight / originStageWidth;

                for (let i = Math.abs(subLeft); i >= 0; i--) {
                    moveSteps.push({
                        left: (i * direction) / (dataType === 'crop-tc' ? 2 : 1),
                        top: (radio * i * direction) / (dataType === 'crop-lc' ? 2 : 1),
                        width: originStageWidth + i * direction,
                        height: originStageHeight + radio * i * direction,
                    });
                }
            } else if (originStageWidth > originStageHeight) {
                const subTop = top - originMainLayerTop;
                const direction = subTop > 0 ? 1 : -1;
                const radio = originStageWidth / originStageHeight;
                for (let i = Math.abs(subTop); i >= 0; i--) {
                    moveSteps.push({
                        left: (radio * i * direction) / (dataType === 'crop-tc' ? 2 : 1),
                        top: (i * direction) / (dataType === 'crop-lc' ? 2 : 1),
                        width: originStageWidth + radio * i * direction,
                        height: originStageHeight + i * direction,
                    });
                }
            }
        } else if ((dataType === 'crop-bc' || dataType === 'crop-rc') && !isStageAutoCrop) {
            if (originStageWidth <= originStageHeight) {
                const subWidth = newWidth - originStageWidth;
                const direction = subWidth > 0 ? 1 : -1;
                const radio = originStageHeight / originStageWidth;
                for (let i = Math.abs(subWidth); i >= 0; i--) {
                    moveSteps.push({
                        left: dataType === 'crop-bc' ? (i * direction) / 2 : 0,
                        top: dataType === 'crop-rc' ? (radio * i * direction) / 2 : 0,
                        width: originStageWidth + i * direction,
                        height: originStageHeight + radio * i * direction,
                    });
                }
            } else if (originStageWidth > originStageHeight) {
                const subHeight = newHeight - originStageHeight;
                const direction = subHeight > 0 ? 1 : -1;
                const radio = originStageWidth / originStageHeight;
                for (let i = Math.abs(subHeight); i >= 0; i--) {
                    moveSteps.push({
                        left: dataType === 'crop-bc' ? (radio * i * direction) / 2 : 0,
                        top: dataType === 'crop-rc' ? (i * direction) / 2 : 0,
                        width: originStageWidth + radio * i * direction,
                        height: originStageHeight + i * direction,
                    });
                }
            }
        } else if (dataType === 'crop-lc') {
            const subLeft = left - originMainLayerLeft;

            const direction = subLeft > 0 ? 1 : -1;
            for (let i = Math.abs(subLeft); i >= 0; i--) {
                moveSteps.push({
                    left: i * direction,
                    top: 0,
                    width: originStageWidth + i * direction,
                    height: originStageHeight,
                });
            }
        } else if (dataType === 'crop-rc') {
            const subWidth = newWidth - originStageWidth;
            const direction = subWidth > 0 ? 1 : -1;
            for (let i = Math.abs(subWidth); i >= 0; i--) {
                moveSteps.push({
                    left: 0,
                    top: 0,
                    width: originStageWidth + i * direction,
                    height: originStageHeight,
                });
            }
        } else if (dataType === 'crop-bc') {
            const subHeight = newHeight - originStageHeight;
            const direction = subHeight > 0 ? 1 : -1;
            for (let i = Math.abs(subHeight); i >= 0; i--) {
                moveSteps.push({
                    left: 0,
                    top: 0,
                    height: originStageHeight + i * direction,
                    width: originStageWidth,
                });
            }
        } else if (dataType === 'crop-tc') {
            const subTop = top - originMainLayerTop;
            const direction = subTop > 0 ? 1 : -1;
            for (let i = Math.abs(subTop); i >= 0; i--) {
                moveSteps.push({
                    left: 0,
                    top: i * direction,
                    width: originStageWidth,
                    height: originStageHeight + i * direction,
                });
            }
        }

        for (let i = 0; i < moveSteps.length; i++) {
            const newMainLayer = cloneDeep(originMainLayer);
            const { left, top, width, height } = moveSteps[i];

            stageSizeChangeAndMoveMainLayer(newMainLayer, left, top, width, height);

            if (!isMainLayerOutOfRange(newMainLayer, this.store.isOutOfStage)) {
                if (shouldCommit) {
                    traverseTrees(originLayers, (layer: ILayerProps) => {
                        const newLayer = cloneDeep(layer);

                        if (layer.layerType === LayerType.MAIN_LAYER) {
                            return;
                        }

                        if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                            newLayer.width = width;
                            newLayer.height = height;
                            this.addOrUpdateLayerBaseInfo({ ...newLayer });
                            return;
                        }

                        newLayer.left = newLayer.left + left;
                        newLayer.top = newLayer.top + top;
                        this.addOrUpdateLayerBaseInfo({ ...newLayer });
                    });

                    const resultMainLayer = formatMainLayerToInt({ ...newMainLayer });
                    this.addOrUpdateLayerBaseInfo(resultMainLayer);
                }

                return {
                    width: newMainLayer.width,
                    height: newMainLayer.height,
                };
            }
        }
    }

    moveScaleStageSizeOperate(
        params: Pick<ILayerProps, 'left' | 'top' | 'width' | 'height'>,
        originMainLayer: ILayerProps,
        originLayers: ILayerProps[],
        dataType: string,
        isStageAutoCrop: boolean,
        shouldCommit = true,
    ) {
        const moveSteps = [] as Pick<ILayerProps, 'left' | 'top' | 'width' | 'height'>[];

        const { left, top, width: newWidth, height: newHeight } = params;
        const {
            left: originMainLayerLeft,
            top: originMainLayerTop,
            width: originStageWidth,
            height: originStageHeight,
        } = originMainLayer;

        if (dataType === 'crop-lt' && !isStageAutoCrop) {
            const subLeft = left - originMainLayerLeft;
            const direction = subLeft > 0 ? 1 : -1;
            const radio = originStageHeight / originStageWidth;

            for (let i = Math.abs(subLeft); i >= 0; i--) {
                moveSteps.push({
                    left: i * direction,
                    top: radio * i * direction,
                    width: originStageWidth + i * direction,
                    height: originStageHeight + radio * i * direction,
                });
            }
        } else if (dataType === 'crop-rt' && !isStageAutoCrop) {
            const subTop = top - originMainLayerTop;
            const direction = subTop > 0 ? 1 : -1;
            const radio = originStageWidth / originStageHeight;
            for (let i = Math.abs(subTop); i >= 0; i--) {
                moveSteps.push({
                    left: 0,
                    top: i * direction,
                    width: originStageWidth + radio * i * direction,
                    height: originStageHeight + i * direction,
                });
            }
        } else if (dataType === 'crop-rb' && !isStageAutoCrop) {
            const subWidth = newWidth - originStageWidth;
            const direction = subWidth > 0 ? 1 : -1;
            const radio = originStageHeight / originStageWidth;

            for (let i = Math.abs(subWidth); i >= 0; i--) {
                moveSteps.push({
                    left: 0,
                    top: 0,
                    width: originStageWidth + i * direction,
                    height: originStageHeight + i * direction * radio,
                });
            }
        } else if (dataType === 'crop-lb' && !isStageAutoCrop) {
            const subLeft = left - originMainLayerLeft;
            const direction = subLeft > 0 ? 1 : -1;
            const radio = originStageHeight / originStageWidth;

            for (let i = Math.abs(subLeft); i >= 0; i--) {
                moveSteps.push({
                    left: i * direction,
                    top: 0,
                    width: originStageWidth + i * direction,
                    height: originStageHeight + radio * i * direction,
                });
            }
        }

        for (let i = 0; i < moveSteps.length; i++) {
            let newMainLayer = cloneDeep(originMainLayer);
            const { left, top, width, height } = moveSteps[i];

            stageSizeChangeAndMoveMainLayer(newMainLayer, left, top, width, height);
            if (!isMainLayerOutOfRange(newMainLayer, this.store.isOutOfStage)) {
                if (shouldCommit) {
                    traverseTrees(originLayers, (layer: ILayerProps) => {
                        const newLayer = cloneDeep(layer);

                        if (layer.layerType === LayerType.MAIN_LAYER) {
                            return;
                        }

                        if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                            newLayer.width = width;
                            newLayer.height = height;
                            this.addOrUpdateLayerBaseInfo({ ...newLayer });
                            return;
                        }

                        newLayer.left = newLayer.left + left;
                        newLayer.top = newLayer.top + top;
                        this.addOrUpdateLayerBaseInfo({ ...newLayer });
                    });
                    newMainLayer = formatMainLayerToInt({ ...newMainLayer });
                    this.addOrUpdateLayerBaseInfo(newMainLayer);
                }

                return {
                    width: newMainLayer.width,
                    height: newMainLayer.height,
                };
            }
        }
    }

    moveStage(movePoint: IPoint, mainLayer: ILayerProps, layers: ILayerProps[], isEnd: boolean) {
        const {
            left: originMainLayerLeft,
            top: originMainLayerTop,
            width: originStageWidth,
            height: originStageHeight,
            rotate,
        } = mainLayer;
        const data = mainLayer.data as IImageLayerProps;
        const { naturalWidth: natureMainLayerWidth, naturalHeight: natureMainLayerHeight, imageTransform } = data;
        const { scale: originMainLayerScale } = imageTransform;
        mainLayer.left = mainLayer.left + movePoint.x;
        mainLayer.top = mainLayer.top + movePoint.y;
        const { originRelativeStagePoint } = getStageAndMainLayerCenterPoint(mainLayer);
        originRelativeStagePoint.y = -originRelativeStagePoint.y; // 坐标方向不同
        if (isEnd) {
            // // 主图中心点以舞台中心点做旋转
            const rotateMainLayerPosition = rotatePoint(
                {
                    x: -originRelativeStagePoint.x,
                    y: -originRelativeStagePoint.y,
                },
                rotate,
            );
            const { movePoint: adjustMovePoint } = adjustCropImgPosition({
                mainLayerSize: {
                    width: originStageWidth,
                    height: originStageHeight,
                },
                adjustLayerSize: {
                    width: natureMainLayerWidth * originMainLayerScale,
                    height: natureMainLayerHeight * originMainLayerScale,
                },
                adjustLayerRotate: mainLayer.rotate,
                adjustLayerRelativePosition: rotateMainLayerPosition,
                ignoreScaleLimit: true,
            });

            // HACK: 移动图层位置，理论上不应该会存在 scale 的变化，这里先临时解决调整舞台尺寸后，移动主图位置时，不会执行回弹的问题
            // @see https://jira.meitu.com/browse/MTXXPRO-53748
            // if (scale === 1) {
            mainLayer.left = mainLayer.left + adjustMovePoint.x;
            mainLayer.top = mainLayer.top - adjustMovePoint.y;
            // }
        }
        traverseTrees(layers, (layer: ILayerProps) => {
            if (
                layer.layerType === LayerType.MAIN_LAYER ||
                layer.layerType === LayerType.BACKGROUND_LAYER ||
                (layer.children && layer.children.length > 0)
            ) {
                return;
            }
            const radio = imageTransform.scale;
            const { left: mainLayerLeft, top: mainLayerTop } = mainLayer;
            const { left: layerLeft, top: layerTop, width: layerWidth, height: layerHeight } = layer;
            const layerRelativeMainLayerPoint = {
                x:
                    layerLeft +
                    layerWidth / 2 -
                    (originMainLayerLeft + (natureMainLayerWidth * originMainLayerScale) / 2),
                y: -(
                    layerTop +
                    layerHeight / 2 -
                    (originMainLayerTop + (natureMainLayerHeight * originMainLayerScale) / 2)
                ),
            };
            const newLayerRelativeMainLayerPoint = {
                x: layerRelativeMainLayerPoint.x * (radio / originMainLayerScale),
                y: layerRelativeMainLayerPoint.y * (radio / originMainLayerScale),
            };
            layer.width = layerWidth * (radio / originMainLayerScale);
            layer.height = layerHeight * (radio / originMainLayerScale);
            const newLayerPosition = {
                x:
                    newLayerRelativeMainLayerPoint.x +
                    (mainLayerLeft + (natureMainLayerWidth * radio) / 2) -
                    layer.width / 2,
                y:
                    -newLayerRelativeMainLayerPoint.y +
                    (mainLayerTop + (natureMainLayerHeight * radio) / 2) -
                    layer.height / 2,
            };
            layer.left = newLayerPosition.x;
            layer.top = newLayerPosition.y;
            this.framework.service.behavior.get(layer)?.onBeforeCrop?.(layer, radio / originMainLayerScale);
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });
        this.addOrUpdateLayerBaseInfo({ ...mainLayer });

        const { width: currentWidth, height: currentHeight } = this.framework.service.edgelessCanvas.getSize();

        const stageScale = computeAutoScale(
            currentWidth - 160,
            currentHeight - 160 - 40,
            mainLayer.width,
            mainLayer.height,
            this.framework.service.edgelessCanvas.fitPadding[0],
        );

        this.store.setCurrentCropStageScale(stageScale);

        filterAndUpdateGroupLayer(layers, (layer: ILayerProps) => {
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });
    }

    flipX(stageIndex?: number) {
        const originIndex = stageIndex === undefined ? this.framework.service.frame.getActiveFrameId() : stageIndex;
        const mainLayer = this.framework.service.frame.getMainLayer(originIndex);
        const layers = this.framework.service.frame.getLayers(originIndex);

        // 防御性代码
        if (!mainLayer) {
            return;
        }

        const { width: originStageWidth, height: originStageHeight } = mainLayer;
        traverseTrees(layers, (layer: ILayerProps) => {
            const { left: layerLeft, top: layerTop, width: layerWidth, height: layerHeight } = layer;
            let width = layerWidth;
            let height = layerHeight;
            if (layer.children && layer.children.length > 0) {
                return;
            }
            if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                return;
            }
            if (layer.layerType === LayerType.MAIN_LAYER) {
                const { naturalHeight, naturalWidth, imageTransform } = layer.data as IImageLayerProps;
                const { scale } = imageTransform;
                width = scale * naturalWidth;
                height = scale * naturalHeight;
            }
            // 主图中心点和舞台的相对位置
            const relativeStagePoSition = {
                x: layerLeft + width / 2 - originStageWidth / 2,
                y: layerTop + height / 2 - originStageHeight / 2,
            };
            const flipXPosition = {
                x: -relativeStagePoSition.x,
                y: relativeStagePoSition.y,
            };
            const newPosition = {
                x: flipXPosition.x + originStageWidth / 2 - width / 2,
                y: flipXPosition.y + originStageHeight / 2 - height / 2,
            };
            layer.rotate = layer.rotate + (360 - layer.rotate * 2);
            layer.left = newPosition.x;
            layer.top = newPosition.y;
            if (layer.layerType === LayerType.TEXT_LAYER) {
                layer.flipH = !layer.flipH;
            } else if (layer.layerType === LayerType.IMAGE_LAYER || layer.layerType === LayerType.MAIN_LAYER) {
                const { imageTransform } = layer.data as IImageLayerProps;
                imageTransform.flipH = !imageTransform.flipH;
            }
            this.addOrUpdateLayerBaseInfo({ ...layer }, false);
            // this.framework.service.frame.setActiveLayer(mainLayer.id);
        });

        this.syncCropFrameViewport(originIndex);
    }

    flipY(stageIndex?: number) {
        const originIndex = stageIndex === undefined ? this.framework.service.frame.getActiveFrameId() : stageIndex;
        const mainLayer = this.framework.service.frame.getMainLayer(originIndex);
        const layers = this.framework.service.frame.getLayers(originIndex);

        // 防御性代码
        if (!mainLayer) {
            return;
        }

        const { width: originStageWidth, height: originStageHeight } = mainLayer;
        traverseTrees(layers, (layer: ILayerProps) => {
            const { left: layerLeft, top: layerTop, width: layerWidth, height: layerHeight } = layer;
            let width = layerWidth;
            let height = layerHeight;
            if (layer.children && layer.children.length > 0) {
                return;
            }
            if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                return;
            }
            if (layer.layerType === LayerType.MAIN_LAYER) {
                const { naturalHeight, naturalWidth, imageTransform } = layer.data as IImageLayerProps;
                const { scale } = imageTransform;
                width = scale * naturalWidth;
                height = scale * naturalHeight;
            }
            // 主图中心点和舞台的相对位置
            const relativeStagePoSition = {
                x: layerLeft + width / 2 - originStageWidth / 2,
                y: layerTop + height / 2 - originStageHeight / 2,
            };
            const flipYPosition = {
                x: relativeStagePoSition.x,
                y: -relativeStagePoSition.y,
            };
            const newPosition = {
                x: flipYPosition.x + originStageWidth / 2 - width / 2,
                y: flipYPosition.y + originStageHeight / 2 - height / 2,
            };
            layer.rotate = -layer.rotate;
            layer.left = newPosition.x;
            layer.top = newPosition.y;
            if (layer.layerType === LayerType.TEXT_LAYER) {
                layer.flipV = !layer.flipV;
            } else if (layer.layerType === LayerType.IMAGE_LAYER || layer.layerType === LayerType.MAIN_LAYER) {
                const { imageTransform } = layer.data as IImageLayerProps;
                imageTransform.flipV = !imageTransform.flipV;
            }
            this.addOrUpdateLayerBaseInfo({ ...layer }, false);
        });
        this.syncCropFrameViewport(originIndex);
    }

    moveChangeStageSize(
        params: Pick<ILayerProps, 'left' | 'top' | 'width' | 'height'>,

        mainLayer: ILayerProps,
        layers: ILayerProps[],
        isEnd: boolean,
    ) {
        const { left: originMainLayerLeft, top: originMainLayerTop, rotate } = mainLayer;
        const data = mainLayer.data as IImageLayerProps;
        const { naturalWidth: natureMainLayerWidth, naturalHeight: natureMainLayerHeight, imageTransform } = data;
        const { scale: originMainLayerScale } = imageTransform;
        const { left, top, width, height } = params;
        mainLayer.left = left;
        mainLayer.top = top;
        mainLayer.height = height;
        mainLayer.width = width;
        if (isEnd) {
            if (
                mainLayer.width > natureMainLayerWidth * originMainLayerScale ||
                mainLayer.height > natureMainLayerHeight * originMainLayerScale
            ) {
                mainLayer.left = (mainLayer.width - natureMainLayerWidth * originMainLayerScale) / 2;
                mainLayer.top = (mainLayer.height - natureMainLayerHeight * originMainLayerScale) / 2;
            }
            const { originRelativeStagePoint } = getStageAndMainLayerCenterPoint(mainLayer);
            originRelativeStagePoint.y = -originRelativeStagePoint.y; // 坐标方向不同
            const rotateMainLayerPosition = rotatePoint(
                {
                    x: -originRelativeStagePoint.x,
                    y: -originRelativeStagePoint.y,
                },
                rotate,
            );
            const { scale, movePoint: adjustMovePoint } = adjustCropImgPosition({
                mainLayerSize: {
                    width: mainLayer.width,
                    height: mainLayer.height,
                },
                adjustLayerSize: {
                    width: natureMainLayerWidth * originMainLayerScale,
                    height: natureMainLayerHeight * originMainLayerScale,
                },
                adjustLayerRotate: mainLayer.rotate,
                adjustLayerRelativePosition: rotateMainLayerPosition,
            });
            if (scale === 1) {
                mainLayer.left = mainLayer.left + adjustMovePoint.x;
                mainLayer.top = mainLayer.top - adjustMovePoint.y;
            } else {
                imageTransform.scale = originMainLayerScale * scale;
                mainLayer.left =
                    mainLayer.left +
                    (natureMainLayerWidth * originMainLayerScale -
                        natureMainLayerWidth * originMainLayerScale * scale) /
                        2;
                mainLayer.top =
                    mainLayer.top +
                    (natureMainLayerHeight * originMainLayerScale -
                        natureMainLayerHeight * originMainLayerScale * scale) /
                        2;
            }
        }
        this.addOrUpdateLayerBaseInfo({ ...mainLayer });
        traverseTrees(layers, (layer: ILayerProps) => {
            if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                backgroundStageChange(layer, mainLayer.width, mainLayer.height);
                this.addOrUpdateLayerBaseInfo({ ...layer });
                return;
            }
            if (layer.layerType === LayerType.MAIN_LAYER || (layer.children && layer.children.length > 0)) {
                return;
            }
            const radio = imageTransform.scale;
            const { left: mainLayerLeft, top: mainLayerTop } = mainLayer;
            const { left: layerLeft, top: layerTop, width: layerWidth, height: layerHeight } = layer;
            const layerRelativeMainLayerPoint = {
                x:
                    layerLeft +
                    layerWidth / 2 -
                    (originMainLayerLeft + (natureMainLayerWidth * originMainLayerScale) / 2),
                y: -(
                    layerTop +
                    layerHeight / 2 -
                    (originMainLayerTop + (natureMainLayerHeight * originMainLayerScale) / 2)
                ),
            };
            const newLayerRelativeMainLayerPoint = {
                x: layerRelativeMainLayerPoint.x * (radio / originMainLayerScale),
                y: layerRelativeMainLayerPoint.y * (radio / originMainLayerScale),
            };
            layer.width = layerWidth * (radio / originMainLayerScale);
            layer.height = layerHeight * (radio / originMainLayerScale);
            const newLayerPosition = {
                x:
                    newLayerRelativeMainLayerPoint.x +
                    (mainLayerLeft + (natureMainLayerWidth * radio) / 2) -
                    layer.width / 2,
                y:
                    -newLayerRelativeMainLayerPoint.y +
                    (mainLayerTop + (natureMainLayerHeight * radio) / 2) -
                    layer.height / 2,
            };
            layer.left = newLayerPosition.x;
            layer.top = newLayerPosition.y;
            this.framework.service.behavior.get(layer)?.onBeforeCrop?.(layer, radio / originMainLayerScale);
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });
        filterAndUpdateGroupLayer(layers, (layer: ILayerProps) => {
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });
    }

    moveScaleStageSize(
        params: Pick<ILayerProps, 'left' | 'top' | 'width' | 'height'>,
        mainLayer: ILayerProps,
        layers: ILayerProps[],
        isEnd: boolean,
    ) {
        const { left: originMainLayerLeft, top: originMainLayerTop, rotate } = mainLayer;
        const data = mainLayer.data as IImageLayerProps;
        const { naturalWidth: natureMainLayerWidth, naturalHeight: natureMainLayerHeight, imageTransform } = data;
        const { scale: originMainLayerScale } = imageTransform;
        const { left, top, width, height } = params;
        mainLayer.left = left;
        mainLayer.top = top;
        mainLayer.height = height;
        mainLayer.width = width;
        if (isEnd) {
            if (
                mainLayer.width > natureMainLayerWidth * originMainLayerScale ||
                mainLayer.height > natureMainLayerHeight * originMainLayerScale
            ) {
                mainLayer.left = (mainLayer.width - natureMainLayerWidth * originMainLayerScale) / 2;
                mainLayer.top = (mainLayer.height - natureMainLayerHeight * originMainLayerScale) / 2;
            }
            const { originRelativeStagePoint } = getStageAndMainLayerCenterPoint(mainLayer);
            originRelativeStagePoint.y = -originRelativeStagePoint.y; // 坐标方向不同
            const rotateMainLayerPosition = rotatePoint(
                {
                    x: -originRelativeStagePoint.x,
                    y: -originRelativeStagePoint.y,
                },
                rotate,
            );
            const { scale, movePoint: adjustMovePoint } = adjustCropImgPosition({
                mainLayerSize: {
                    width: mainLayer.width,
                    height: mainLayer.height,
                },
                adjustLayerSize: {
                    width: natureMainLayerWidth * originMainLayerScale,
                    height: natureMainLayerHeight * originMainLayerScale,
                },
                adjustLayerRotate: mainLayer.rotate,
                adjustLayerRelativePosition: rotateMainLayerPosition,
            });
            if (scale === 1) {
                mainLayer.left = mainLayer.left + adjustMovePoint.x;
                mainLayer.top = mainLayer.top - adjustMovePoint.y;
            } else {
                imageTransform.scale = originMainLayerScale * scale;
                mainLayer.left =
                    mainLayer.left +
                    (natureMainLayerWidth * originMainLayerScale -
                        natureMainLayerWidth * originMainLayerScale * scale) /
                        2;
                mainLayer.top =
                    mainLayer.top +
                    (natureMainLayerHeight * originMainLayerScale -
                        natureMainLayerHeight * originMainLayerScale * scale) /
                        2;
            }
        }

        this.addOrUpdateLayerBaseInfo({ ...mainLayer });
        traverseTrees(layers, (layer: ILayerProps) => {
            if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                backgroundStageChange(layer, mainLayer.width, mainLayer.height);
                this.addOrUpdateLayerBaseInfo({ ...layer });
                return;
            }
            if (layer.layerType === LayerType.MAIN_LAYER || (layer.children && layer.children.length > 0)) {
                return;
            }
            const radio = imageTransform.scale;
            const { left: mainLayerLeft, top: mainLayerTop } = mainLayer;
            const { left: layerLeft, top: layerTop, width: layerWidth, height: layerHeight } = layer;
            const layerRelativeMainLayerPoint = {
                x:
                    layerLeft +
                    layerWidth / 2 -
                    (originMainLayerLeft + (natureMainLayerWidth * originMainLayerScale) / 2),
                y: -(
                    layerTop +
                    layerHeight / 2 -
                    (originMainLayerTop + (natureMainLayerHeight * originMainLayerScale) / 2)
                ),
            };
            const newLayerRelativeMainLayerPoint = {
                x: layerRelativeMainLayerPoint.x * (radio / originMainLayerScale),
                y: layerRelativeMainLayerPoint.y * (radio / originMainLayerScale),
            };
            layer.width = layerWidth * (radio / originMainLayerScale);
            layer.height = layerHeight * (radio / originMainLayerScale);
            const newLayerPosition = {
                x:
                    newLayerRelativeMainLayerPoint.x +
                    (mainLayerLeft + (natureMainLayerWidth * radio) / 2) -
                    layer.width / 2,
                y:
                    -newLayerRelativeMainLayerPoint.y +
                    (mainLayerTop + (natureMainLayerHeight * radio) / 2) -
                    layer.height / 2,
            };
            layer.left = newLayerPosition.x;
            layer.top = newLayerPosition.y;
            this.framework.service.behavior.get(layer)?.onBeforeCrop?.(layer, radio / originMainLayerScale);
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });
        filterAndUpdateGroupLayer(layers, (layer: ILayerProps) => {
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });
    }

    setMask(maskURL: string, materialId: string) {
        const originIndex = this.framework.service.frame.getActiveFrameId();
        const mainLayer = cloneDeep(this.framework.service.frame.getMainLayer(originIndex));

        // 防御性代码
        if (!mainLayer) {
            return;
        }

        const imageData = mainLayer.data as IImageLayerProps;

        imageData.maskURL = maskURL;

        if (!mainLayer.materialIds) {
            mainLayer.materialIds = {};
        }

        const maskIds = mainLayer.materialIds[MaterialIdsType.MASK] || [];

        maskIds[0] = String(materialId);
        mainLayer.materialIds[MaterialIdsType.MASK] = maskIds;

        // ATTENTION: 舞台要整个重新渲染，才可以让插件中的 RenderStage 可以再次更新，才可以渲染舞台的蒙版裁剪效果
        this.addOrUpdateLayerBaseInfo(mainLayer);
        // this.framework.service.frame.rerender(originIndex);
    }

    async getImageToEdgeByLayers(layers: ILayerProps[], mainLayer: ILayerProps) {
        const { left: mainLayerLeft, top: mainLayerTop } = mainLayer;
        const layersBoundingBox = layers
            .filter(
                (layer) => layer.layerType !== LayerType.MAIN_LAYER && layer.layerType !== LayerType.BACKGROUND_LAYER,
            )
            .map((layer) => getLayerBoundingBox(layer));
        const points = flatten(layersBoundingBox);
        let minX = Math.min(...points.map((point) => point[0]));
        let minY = Math.min(...points.map((point) => point[1]));
        let maxX = Math.max(...points.map((point) => point[0]));
        let maxY = Math.max(...points.map((point) => point[1]));

        if (minX < 0) {
            minX = 0;
        }

        if (minY < 0) {
            minY = 0;
        }

        if (maxX > mainLayer.width) {
            maxX = mainLayer.width;
        }

        if (maxY > mainLayer.height) {
            maxY = mainLayer.height;
        }

        const newWidth = maxX - minX;
        const newHeight = maxY - minY;

        const newLeft = mainLayerLeft - minX;
        const newTop = mainLayerTop - minY;

        const newMainLayer = cloneDeep(mainLayer);
        newMainLayer.left = newLeft;
        newMainLayer.top = newTop;
        newMainLayer.width = newWidth;
        newMainLayer.height = newHeight;
        this.addOrUpdateLayerBaseInfo(newMainLayer);

        traverseTrees(layers, (layer: ILayerProps) => {
            const newLayer = cloneDeep(layer);

            if (layer.layerType === LayerType.MAIN_LAYER || layer.layerType === LayerType.GROUP_LAYER) {
                return;
            }

            if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                backgroundStageChange(newLayer, newMainLayer.width, newMainLayer.height);
                this.addOrUpdateLayerBaseInfo({ ...newLayer });
                return;
            }

            layer.left = layer.left - minX;
            layer.top = layer.top - minY;
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });
    }

    async getImageToEdgeByMainLayer(mainLayer: ILayerProps, stageIndex?: number) {
        const { naturalWidth, naturalHeight, url } = mainLayer.data as IMainLayerProps;
        const image = await loadImageAsync(url);
        const canvas = document.createElement('canvas');
        canvas.width = naturalWidth;
        canvas.height = naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(image, 0, 0, naturalWidth, naturalHeight);
        const result = await clearImageEdgeBlank({ image: canvas });

        const newMainLayer = cloneDeep(mainLayer);
        newMainLayer.left = -result.x;
        newMainLayer.top = -result.y;
        newMainLayer.width = result.width;
        newMainLayer.height = result.height;
        const data = newMainLayer.data as IMainLayerProps;
        data.imageTransform.scale = 1;
        data.imageTransform.rotate = 0;
        data.imageTransform.flipH = false;
        data.imageTransform.flipV = false;
        newMainLayer.rotate = 0;
        this.addOrUpdateLayerBaseInfo(newMainLayer);

        const originIndex = stageIndex === undefined ? this.framework.service.frame.getActiveFrameId() : stageIndex;
        const layers = this.framework.service.frame.getLayers(originIndex);
        traverseTrees(layers, (layer: ILayerProps) => {
            const newLayer = cloneDeep(layer);

            if (layer.layerType === LayerType.MAIN_LAYER || layer.layerType === LayerType.GROUP_LAYER) {
                return;
            }

            if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                backgroundStageChange(newLayer, newMainLayer.width, newMainLayer.height);
                this.addOrUpdateLayerBaseInfo({ ...newLayer });
                return;
            }

            // 旋转会0度
            // 缩放图层、重新计算位置
            const relativePosition = getLayerCenterPositionRelateToMainLayerCenterPosition(layer, mainLayer);

            const subScale = 1 / (mainLayer.data as IImageLayerProps).imageTransform.scale;
            const newRelativePosition = revertPointY(
                rotatePoint(
                    flipPoint(
                        revertPointY({
                            x: relativePosition.x * subScale,
                            y: relativePosition.y * subScale,
                        }),
                        (mainLayer.data as IImageLayerProps).imageTransform.flipH,
                        (mainLayer.data as IImageLayerProps).imageTransform.flipV,
                    ),
                    mainLayer.rotate,
                ),
            );

            const newMainLayerCenterPoint = getMainLayerCenterPosition(newMainLayer);
            this.framework.service.frame.scaleLayer(layer, layer.width * subScale - layer.width);

            layer.left = newRelativePosition.x + newMainLayerCenterPoint.x - layer.width / 2;
            layer.top = newRelativePosition.y + newMainLayerCenterPoint.y - layer.height / 2;
            layer.rotate = layer.rotate - mainLayer.rotate;
            this.addOrUpdateLayerBaseInfo({ ...layer });
        });
    }

    onConfirm(callback: (value: boolean) => void) {
        const handler = this.framework.service.events.on(CROP_STAGE_EVENT.CONFIRM, callback);

        return () => {
            this.framework.service.events.off(handler);
        };
    }

    onCancel(callback: (value: boolean) => void) {
        const handler = this.framework.service.events.on(CROP_STAGE_EVENT.CANCEL, callback);

        return () => {
            this.framework.service.events.off(handler);
        };
    }
}
